import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTruck, faUser, faWeightHanging, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors.jsx';
import { useTranslation } from 'react-i18next';

const buildStatusMeta = (statuses) => ({
    PENDING:            { label: statuses.pending,   bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
    SUPERVISOR_ASSIGNED:{ label: statuses.assigned,  bg: '#ede9fe', text: '#6d28d9', dot: '#7c3aed' },
    LOADING:            { label: statuses.loading,   bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6' },
    LOADED:             { label: statuses.loaded,    bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
    IN_TRANSIT:         { label: statuses.inTransit, bg: '#e0f2fe', text: '#0369a1', dot: '#0ea5e9' },
    DELIVERED:          { label: statuses.delivered, bg: '#f0fdf4', text: '#15803d', dot: '#22c55e' },
    CANCELLED:          { label: statuses.cancelled, bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
});

const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const STATUS_META = buildStatusMeta(t('salesAnalytics.mySubOrders.statuses', { returnObjects: true }));
    const m = STATUS_META[status] || { label: status ?? t('common.symbols.emDash'), bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' };
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: m.bg, color: m.text }}>
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: m.dot }} />
            {m.label}
        </span>
    );
};

const fmt = (val, emDash) => {
    const n = parseFloat(val);
    if (!val || isNaN(n)) return emDash;
    return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const MySubOrdersTable = ({ items = [], loading, onSelectOrder }) => {
    const { t } = useTranslation();
    const strings = t('salesAnalytics.mySubOrders', { returnObjects: true });
    const emDash = t('common.symbols.emDash');
    if (loading) {
        return (
            <div className="flex items-center justify-center py-16 gap-3" style={{ color: UI_COLORS.TEXT.MUTED }}>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
                <span className="text-sm font-medium">{strings.loading}</span>
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                <FontAwesomeIcon icon={faTruck} className="text-3xl opacity-30" />
                <p className="text-sm">{strings.empty}</p>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {items.map((so) => {
                const hasSupervisor = !!so.supervisor_name;
                return (
                    <div
                        key={so.id ?? so.sub_order_number}
                        className="rounded-xl border p-4 flex items-center gap-3 cursor-pointer transition-all hover:shadow-md hover:border-gray-300"
                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
                        onClick={() => onSelectOrder?.(so)}
                    >
                        {/* Status dot column */}
                        <div className="flex-shrink-0">
                            <StatusBadge status={so.status} />
                        </div>

                        {/* Main info */}
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {so.party_name ?? so.customer_name ?? so.tally_name ?? so.sub_order_number ?? emDash}
                            </p>
                            <p className="text-xs truncate" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {so.sub_order_number ?? so.order_number ?? ''}
                            </p>
                        </div>

                        {/* Vehicle */}
                        <div className="hidden sm:flex flex-col items-center flex-shrink-0 min-w-[80px]">
                            <FontAwesomeIcon icon={faTruck} className="text-xs mb-0.5" style={{ color: UI_COLORS.TEXT.MUTED }} />
                            <p className="text-xs font-semibold text-center" style={{ color: so.vehicle_number ? UI_COLORS.TEXT.PRIMARY : UI_COLORS.TEXT.MUTED }}>
                                {so.vehicle_number ?? emDash}
                            </p>
                        </div>

                        {/* Weight */}
                        <div className="hidden sm:flex flex-col items-center flex-shrink-0 min-w-[70px]">
                            <FontAwesomeIcon icon={faWeightHanging} className="text-xs mb-0.5" style={{ color: UI_COLORS.TEXT.MUTED }} />
                            <p className="text-xs font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {fmt(so.total_weight ?? so.weight, emDash)} <span className="font-normal text-gray-400">{strings.kg}</span>
                            </p>
                        </div>

                        {/* Supervisor */}
                        <div className="hidden md:flex flex-col items-center flex-shrink-0 min-w-[100px]">
                            <FontAwesomeIcon icon={faUser} className="text-xs mb-0.5" style={{ color: UI_COLORS.TEXT.MUTED }} />
                            <p className="text-xs font-semibold text-center truncate max-w-[100px]"
                                style={{ color: hasSupervisor ? UI_COLORS.TEXT.PRIMARY : UI_COLORS.TEXT.MUTED }}>
                                {so.supervisor_name ?? strings.unassigned}
                            </p>
                        </div>

                        <FontAwesomeIcon icon={faChevronRight} className="text-xs flex-shrink-0" style={{ color: UI_COLORS.TEXT.MUTED }} />
                    </div>
                );
            })}
        </div>
    );
};

export default MySubOrdersTable;
